import { useLingui } from "@lingui/react";
import { useLinkProps } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { formatEventDate, formatEventTime } from "@/features/Events/utils";
import {
  EventCard,
  EventCardProps,
} from "@whoosh/design-system/src/apps/PatronApp/components/EventCard/EventCard";
import { EventStatusType } from "@whoosh/design-system/src/apps/PatronApp/components/EventCard/EventStatus";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { useEventLocationLabel } from "../../Events/hooks/useEventLocationLabel";
import {
  EventCardWrapper$key,
  ClientEventSignUpAccess,
  ClientEventSignUpStatus,
} from "./__generated__/EventCardWrapper.graphql";

type EventCardWrapperProps = {
  eventCardWrapperRef: EventCardWrapper$key | null;
  isFeatured?: boolean;
};

const matchEventStatus = ({
  signUpStatusForClient,
  accessStatusForClient,
}: {
  signUpStatusForClient?: ClientEventSignUpStatus | null;
  accessStatusForClient?: ClientEventSignUpAccess | null;
}): EventStatusType => {
  const status =
    signUpStatusForClient !== "NONE"
      ? signUpStatusForClient
      : accessStatusForClient;

  const statusMap: Record<string, EventStatusType> = {
    CHECKED_IN: "CONFIRMED",
    CONFIRMED: "CONFIRMED",
    CLOSED: "CLOSED",
    FULL: "FULL",
    PENDING: "PENDING",
    SIGNUP_AVAILABLE: "SIGNUP_AVAILABLE",
    WAITLIST_AVAILABLE: "WAITLIST_AVAILABLE",
    WAITLIST: "ON_WAITLIST",
  };

  return statusMap[status || ""] || "COMPLETE";
};

export const EventCardWrapper = ({
  eventCardWrapperRef,
  isFeatured = false,
}: EventCardWrapperProps) => {
  const { i18n } = useLingui();
  const event = useFragment(
    EventCardWrapperFragmentString,
    eventCardWrapperRef
  );

  const locationSessionRef = event?.eventSessions?.edges?.[0]?.node ?? null;
  const locationLabel = useEventLocationLabel({
    eventSessionRef: locationSessionRef,
  });

  const { onPress } = useLinkProps({
    screen: "EventDetail",
    params: {
      eventID: event?.id,
    },
  });

  const eventSession = event?.eventSessions?.edges?.[0]?.node ?? null;

  const eventTime = formatEventTime({
    displayTime: eventSession?.displayTime,
    startAt: eventSession?.startAt,
    endAt: eventSession?.endAt,
  });

  const formattedEvent: EventCardProps = {
    subTitle: event?.eventSeries?.name || "",
    eventDate: formatEventDate(event?.startOn || "", i18n.locale),
    eventTime,
    onPress: onPress,
    eventColor: event?.eventSeries?.color || "A",
    publicId: event?.eventSeries?.image?.publicId || "",
    eventStatus: matchEventStatus({
      signUpStatusForClient: event?.signUpStatusForClient,
      accessStatusForClient: event?.accessStatusForClient,
    }),
    opensAt:
      event?.clientEligibleSignUpWindows?.filter((suw) => !!suw?.opensAt)?.[0]
        ?.opensAt || "",
    departmentType: event?.eventSeries?.facility?.department?.type || "GOLF",
    departmentName: event?.eventSeries?.facility?.department?.name || "Golf",
    spotsAvailable: event?.spotsAvailable || 0,
    tags: event?.tags?.edges?.map((tag) => tag?.node?.name ?? "") || [],
    locationLabel,
  };

  return (
    <Box maxWidth={{ base: "100%", lg: "auto" }}>
      <EventCard {...formattedEvent} isFeatured={isFeatured} />
    </Box>
  );
};

export const EventCardWrapperFragmentString = graphql`
  fragment EventCardWrapper on Event {
    id
    accessStatusForClient(clientId: $memberId)
    signUpStatusForClient(clientId: $memberId)
    startOn
    endOn
    spotsAvailable
    tags(first: 1000) {
      edges {
        node {
          name
        }
      }
    }
    eventSessions(first: 1000) {
      edges {
        node {
          startAt
          endAt
          displayTime
          courses {
            edges {
              node {
                id
                name
                courseBucket {
                  name
                }
              }
            }
          }
          ...useEventLocationLabel_Event
        }
      }
    }
    clientEligibleSignUpWindows(clientId: $memberId) {
      id
      closesAt
      opensAt
    }
    eventSeries {
      name
      color
      facility {
        copyOverrides {
          course {
            plural
          }
        }
        department {
          type
          name
        }
      }
      image {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
    }
  }
`;
